import { useCallback, useEffect, useState } from 'react';
import {
  $getSelection,
  $isRangeSelection,
  $isElementNode,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  COMMAND_PRIORITY_LOW,
  SELECTION_CHANGE_COMMAND,
  TextFormatType,
  ElementFormatType,
} from 'lexical';
import {
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from '@lexical/list';
import { $isListNode, ListNode } from '@lexical/list';
import {
  $patchStyleText,
  $getSelectionStyleValueForProperty,
} from '@lexical/selection';
import { $isLinkNode } from '@lexical/link';
import { $getNearestNodeOfType, $findMatchingParent } from '@lexical/utils';
import { IconButton } from '@socialchorus/shared-ui-components';
import ToolbarColorPicker from './toolbar-color-picker';
import AlignmentDropdown from './alignment-dropdown';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

interface ActiveState {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
  fontColor: string;
  listType: 'bullet' | 'number' | null;
  elementFormat: ElementFormatType;
}

function ToolbarButton({
  onClick,
  iconName,
  isActive = false,
  disabled = false,
  title = '',
}: {
  onClick: () => void;
  iconName: string;
  isActive?: boolean;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <IconButton
      onClick={onClick}
      size="extra-compact"
      iconName={iconName}
      title={title}
      disabled={disabled}
      className={isActive ? 'lexical-toolbar__btn--active' : ''}
    />
  );
}

export default function RichTextEditorToolbar() {
  const [editor] = useLexicalComposerContext();
  const [activeState, setActiveState] = useState<ActiveState>({
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
    listType: null,
    fontColor: '#000000',
    elementFormat: '' as ElementFormatType,
  });

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return;

    const anchorNode = selection.anchor.getNode();
    const parent = anchorNode.getParent();

    setActiveState({
      bold: selection.hasFormat('bold'),
      italic: selection.hasFormat('italic'),
      underline: selection.hasFormat('underline'),
      strikethrough: selection.hasFormat('strikethrough'),
      fontColor: $getSelectionStyleValueForProperty(
        selection,
        'color',
        '#000000'
      ),
      elementFormat: (() => {
        let matchingParent;
        if ($isLinkNode(parent)) {
          matchingParent = $findMatchingParent(
            anchorNode,
            (parentNode) => $isElementNode(parentNode) && !parentNode.isInline()
          );
        }
        if ($isElementNode(matchingParent)) {
          return matchingParent.getFormatType();
        } else if ($isElementNode(anchorNode)) {
          return anchorNode.getFormatType();
        }
        return parent?.getFormatType() || 'left';
      })() as ElementFormatType,
      listType: (() => {
        const listNode = $isListNode(parent)
          ? parent
          : $getNearestNodeOfType(anchorNode, ListNode);
        if (listNode) {
          return listNode.getListType() === 'number' ? 'number' : 'bullet';
        }
        return null;
      })(),
    });
  }, []);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateToolbar();
        return false;
      },
      COMMAND_PRIORITY_LOW
    );
  }, [editor, updateToolbar]);

  const formatText = (format: TextFormatType) => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
  };

  const toggleBulletList = () => {
    if (activeState.listType === 'bullet') {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    }
  };

  const onFontColorSelect = useCallback(
    (value: string) => {
      editor.update(() => {
        const selection = $getSelection();
        if (selection !== null) {
          $patchStyleText(selection, { color: value });
        }
      });
    },
    [editor]
  );

  const formatAlignment = (format: ElementFormatType) => {
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, format);
  };

  return (
    <div className="lexical-footer" role="toolbar" aria-label="Formatting">
      <ToolbarButton
        onClick={() => formatText('bold')}
        title="Bold"
        iconName="format_bold"
        isActive={activeState.bold}
      />
      <ToolbarButton
        onClick={() => formatText('italic')}
        title="Italic"
        iconName="format_italic"
        isActive={activeState.italic}
      />
      <ToolbarButton
        onClick={() => formatText('underline')}
        title="Underline"
        iconName="format_underlined"
        isActive={activeState.underline}
      />
      <ToolbarButton
        onClick={() => formatText('strikethrough')}
        isActive={activeState.strikethrough}
        title="Strikethrough"
        iconName="format_strikethrough"
      />

      <ToolbarColorPicker
        color={activeState.fontColor}
        onChange={onFontColorSelect}
      />

      <span className="lexical-toolbar__separator" />

      <ToolbarButton
        onClick={toggleBulletList}
        isActive={activeState.listType === 'bullet'}
        title="Bullet list"
        iconName="format_list_bulleted"
      />

      <AlignmentDropdown
        value={activeState.elementFormat}
        onChange={formatAlignment}
      />
    </div>
  );
}
